import { useState } from "react";
import api from "../../../../../features/auth/axiosConfig";
import { notifyError, notifySuccess } from "src/assets/toastService";
import { StaffFormData } from "src/shared/services/interfacev2";

export const useStaffOperations = (refetch: () => void) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const buildFormData = (formData: StaffFormData) => {
    const fd = new FormData();

    fd.append("firstName", formData.firstName);
    fd.append("middleName", formData.middleName || "");
    fd.append("lastName", formData.lastName || "");

    // ✅ SEND ARRAYS AS JSON
    fd.append("phones", JSON.stringify(formData.phoneNumber ? [formData.phoneNumber] : []));

    fd.append(
      "emails",
      JSON.stringify(
        formData.emails
          ? formData.emails
              .split(",")
              .map((e) => e.trim())
              .filter(Boolean)
          : [],
      ),
    );

    if (formData.image instanceof File) {
      fd.append("image", formData.image);
    }

    return fd;
  };

  // --------------------------
  // ADD STAFF
  // --------------------------
  const addStaff = async (formData: StaffFormData) => {
    setIsProcessing(true);

    try {
      const apiFormData = buildFormData(formData);

      await api.post("/app/pos/v2/staff", apiFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      await refetch();

      notifySuccess("Staff added successfully!");

      return true;
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || "Failed to add staff.";

      notifyError(message);

      return false;
    } finally {
      setTimeout(() => {
        setIsProcessing(false);
      }, 300);
    }
  };

  // --------------------------
  // UPDATE STAFF
  // --------------------------
  const updateStaff = async (id: string, formData: StaffFormData) => {
    setIsProcessing(true);

    try {
      const apiFormData = buildFormData(formData);

      await api.put(`/app/pos/v2/staff/${id}`, apiFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      await refetch();

      notifySuccess("Staff updated successfully!");

      return true;
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || "Failed to update staff.";

      notifyError(message);

      return false;
    } finally {
      setTimeout(() => {
        setIsProcessing(false);
      }, 300);
    }
  };

  // --------------------------
  // DELETE STAFF
  // --------------------------
  const deleteStaff = async (id: string) => {
    setIsProcessing(true);

    try {
      await api.delete(`/app/pos/v2/staff/${id}`);

      // REFRESH DATA FIRST
      await refetch();

      // SHOW SUCCESS TOAST
      notifySuccess("Staff deleted successfully!");

      // SMALL DELAY
      setTimeout(() => {
        setIsProcessing(false);
      }, 300);

      return true;
    } catch (error: any) {
      const message = error?.response?.data?.message;

      notifyError(message || "Failed to delete staff.");

      return false;
    } finally {
      setTimeout(() => {
        setIsProcessing(false);
      }, 300);
    }
  };

  return {
    addStaff,
    updateStaff,
    deleteStaff,
    isProcessing,
  };
};
